import { type AccountSummary, Provider } from '@fruitful/types';
import React, { useEffect, useMemo, useState } from 'react';
import {
    BodyLargeEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    ScrollView,
    Spinner,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import { GroveSimpleModal } from '../../../common/components/GroveSimpleModal';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../../common/components/Toasts';
import { centsToFormattedDollars } from '../../../common/utils/formatting';
import { type MembershipDetails } from '../../entityTypes';
import { useSetSpinwheelCreditCardQuilttAccountIdAndRefetch } from '../hooks/externalAccounts/useSetSpinwheelCreditCardQuilttAccountId';
import { useSetSpinwheelLoanQuilttAccountIdAndRefetch } from '../hooks/externalAccounts/useSetSpinwheelLoanQuilttAccountId';

// Types for merged account data
interface SpinwheelAccountData {
    id: string;
    displayName?: string;
    servicerName?: string;
    ccNumberMasked?: string;
    ccLastFour?: string;
    outstandingBalanceCents?: number;
    minimumPaymentAmountCents?: number;
    interestRate?: number;
    description?: string;
    debtType?: 'SECURED' | 'UNSECURED';
    quilttAccountId?: string | null;
}

interface QuilttAccountData {
    id: string;
    name?: string;
    institutionName?: string;
    accountNumberMasked?: string;
    currentBalanceCents?: number;
    minimumPayment?: number;
    interestRate?: number;
    description?: string;
    type?: string;
    quilttAccountId?: string;
}

const InfoRow = ({
    label,
    value,
}: {
    label: string;
    value: string | React.ReactNode;
}) => (
    <XStack justifyContent="space-between" alignItems="flex-start" gap={16}>
        <BodySmallRegular color="$gray900" minWidth={140}>
            {label}
        </BodySmallRegular>
        <BodyMediumRegular flex={1} textAlign="right">
            {value}
        </BodyMediumRegular>
    </XStack>
);

const AccountInfoPanel = ({
    account,
    title,
}: {
    account: AccountSummary;
    title: string;
}) => (
    <YStack
        flex={1}
        gap={10}
        padding={16}
        borderRadius={16}
        borderWidth={2}
        borderColor="$gray600"
        backgroundColor="$gray200"
    >
        <BodyMediumRegular fontWeight="600" marginBottom={4}>
            {title}
        </BodyMediumRegular>
        <YStack gap={8}>
            <InfoRow label="Account Nickname" value={account.name ?? 'N/A'} />
            <InfoRow
                label="Institution"
                value={account.institutionName ?? 'N/A'}
            />
            <InfoRow
                label="Account Number"
                value={
                    account.accountNumberMasked != null &&
                    account.accountNumberMasked !== ''
                        ? `****${account.accountNumberMasked}`
                        : 'N/A'
                }
            />
            <InfoRow
                label="Category"
                value={
                    account.category != null
                        ? account.category
                              .replace(/_/g, ' ')
                              .toLowerCase()
                              .replace(/\b\w/g, (c) => c.toUpperCase())
                        : 'N/A'
                }
            />
            <InfoRow
                label="Balance"
                value={
                    account.currentBalanceCents != null
                        ? centsToFormattedDollars(account.currentBalanceCents)
                        : 'N/A'
                }
            />
            {account.provider === Provider.SPINWHEEL && (
                <>
                    {account.minimumPayment != null && (
                        <InfoRow
                            label="Min Payment"
                            value={centsToFormattedDollars(
                                account.minimumPayment,
                            )}
                        />
                    )}
                    {account.interestRate != null && (
                        <InfoRow
                            label="Interest Rate"
                            value={`${account.interestRate}%`}
                        />
                    )}
                </>
            )}
            {account.description != null && account.description !== '' && (
                <InfoRow label="Description" value={account.description} />
            )}
            <InfoRow
                label="Account Type"
                value={account.type ?? account.rawType ?? 'N/A'}
            />
        </YStack>
    </YStack>
);

export const AccountMergeModal = ({
    account,
    membershipDetails,
    isOpen,
    onClose,
}: {
    account: AccountSummary;
    membershipDetails: MembershipDetails;
    isOpen: boolean;
    onClose: () => void;
}) => {
    // Check if account is already merged (has both spinwheelAccount and quilttAccount)
    const accountWithExtras = account as AccountSummary & {
        spinwheelAccount?: { id: string; quilttAccountId?: string | null };
        quilttAccount?: { id: string; quilttAccountId?: string };
        quilttAccountId?: string; // The actual Quiltt account ID (not the holistic account ID)
    };

    const isMerged =
        accountWithExtras.spinwheelAccount != null &&
        accountWithExtras.quilttAccount != null;

    console.log('[AccountMergeModal] Account merge status:', {
        isMerged,
        accountProvider: account.provider,
        accountId: account.id,
        hasSpinwheelAccount: !!accountWithExtras.spinwheelAccount,
        hasQuilttAccount: !!accountWithExtras.quilttAccount,
        spinwheelAccountId: accountWithExtras.spinwheelAccount?.id,
        quilttAccountId: accountWithExtras.quilttAccount?.id,
    });

    // Get the merged account ID based on the provider
    const getMergedAccountId = (): string | null => {
        if (!isMerged) return null;

        if (account.provider === Provider.QUILTT) {
            // If main account is Quiltt, the merged account is the Spinwheel one
            return accountWithExtras.spinwheelAccount?.id ?? null;
        } else if (account.provider === Provider.SPINWHEEL) {
            // If main account is Spinwheel, the merged account is the Quiltt one
            // Use quilttAccountId from spinwheelAccount if available, otherwise use quilttAccount.id
            const quilttAccountId =
                accountWithExtras.spinwheelAccount?.quilttAccountId;
            if (quilttAccountId != null && quilttAccountId !== '') {
                return quilttAccountId;
            }
            return accountWithExtras.quilttAccount?.id ?? null;
        }

        return null;
    };

    const [selectedMergeAccountId, setSelectedMergeAccountId] = useState<
        string | null
    >(getMergedAccountId());

    // Reset selected account when modal opens/closes or account changes
    useEffect(() => {
        if (isOpen) {
            if (isMerged) {
                // If account is already merged, set to the merged account ID so we can display it
                const mergedId = getMergedAccountId();
                setSelectedMergeAccountId(mergedId);
            } else {
                // Otherwise, set to the merged account ID if available
                const mergedId = getMergedAccountId();
                setSelectedMergeAccountId(mergedId);
            }
        }
    }, [isOpen, account.id, isMerged]);

    const holisticSummary = membershipDetails.holisticSummary;

    // Get all accounts from holistic summary
    const allAccounts = useMemo(() => {
        if (!holisticSummary) return [];
        const accounts: AccountSummary[] = [];
        Object.values(holisticSummary).forEach((category) => {
            if (category?.accounts != null) {
                accounts.push(...category.accounts);
            }
        });
        return accounts;
    }, [holisticSummary]);

    // Filter accounts by opposite provider and only show accounts that are NOT merged
    const availableMergeAccounts = useMemo(() => {
        const oppositeProvider =
            account.provider === Provider.SPINWHEEL
                ? Provider.QUILTT
                : Provider.SPINWHEEL;
        return allAccounts.filter((acc) => {
            // Check if account is merged (has both spinwheelAccount and quilttAccount)
            const accWithExtras = acc as AccountSummary & {
                spinwheelAccount?: unknown;
                quilttAccount?: unknown;
            };
            const isAccMerged =
                accWithExtras.spinwheelAccount != null &&
                accWithExtras.quilttAccount != null;

            return (
                acc.provider === oppositeProvider &&
                acc.id !== account.id &&
                acc.category === account.category && // Only show accounts in same category
                !isAccMerged // Only show accounts that are NOT merged
            );
        });
    }, [allAccounts, account]);

    // Get the merged account - either from availableMergeAccounts or from the account's merged properties
    const selectedMergeAccount = useMemo(() => {
        console.log('[AccountMergeModal] Computing selectedMergeAccount:', {
            selectedMergeAccountId,
            availableMergeAccountsCount: availableMergeAccounts.length,
            availableMergeAccountIds: availableMergeAccounts.map(
                (acc) => acc.id,
            ),
        });

        if (selectedMergeAccountId == null || selectedMergeAccountId === '') {
            return null;
        }

        // First try to find in availableMergeAccounts
        const foundAccount = availableMergeAccounts.find(
            (acc) => acc.id === selectedMergeAccountId,
        );
        console.log(
            '[AccountMergeModal] Found account in availableMergeAccounts:',
            {
                found: !!foundAccount,
                accountId: foundAccount?.id,
            },
        );
        if (foundAccount) return foundAccount;

        // If not found and account is merged, try to get from merged properties
        if (isMerged) {
            if (account.provider === Provider.QUILTT) {
                // Main account is Quiltt, get Spinwheel account
                const spinwheelAccount = accountWithExtras.spinwheelAccount;
                if (spinwheelAccount?.id === selectedMergeAccountId) {
                    // Convert spinwheelAccount to AccountSummary format
                    const spinwheelData =
                        spinwheelAccount as SpinwheelAccountData;
                    const accountNumberMasked =
                        spinwheelData.ccNumberMasked != null
                            ? spinwheelData.ccNumberMasked
                                  .replace(/\*/g, '')
                                  .slice(-4)
                            : (spinwheelData.ccLastFour ?? undefined);
                    const accountSummary: AccountSummary = {
                        id: spinwheelData.id,
                        provider: Provider.SPINWHEEL,
                        name: spinwheelData.displayName ?? 'N/A',
                        institutionName: spinwheelData.servicerName ?? 'N/A',
                        accountNumberMasked,
                        category: account.category,
                        currentBalanceCents:
                            spinwheelData.outstandingBalanceCents,
                        minimumPayment: spinwheelData.minimumPaymentAmountCents,
                        interestRate: spinwheelData.interestRate,
                        description: spinwheelData.description,
                        type:
                            spinwheelData.debtType === 'SECURED'
                                ? 'SECURED'
                                : 'UNSECURED',
                    };
                    return accountSummary;
                }
            } else if (account.provider === Provider.SPINWHEEL) {
                // Main account is Spinwheel, get Quiltt account
                const quilttAccount = accountWithExtras.quilttAccount;
                if (quilttAccount?.id === selectedMergeAccountId) {
                    // Convert quilttAccount to AccountSummary format
                    const quilttData = quilttAccount as QuilttAccountData;
                    const accountSummary: AccountSummary = {
                        id: quilttData.id,
                        provider: Provider.QUILTT,
                        name: quilttData.name ?? 'N/A',
                        institutionName: quilttData.institutionName ?? 'N/A',
                        accountNumberMasked: quilttData.accountNumberMasked,
                        category: account.category,
                        currentBalanceCents: quilttData.currentBalanceCents,
                        minimumPayment: quilttData.minimumPayment,
                        interestRate: quilttData.interestRate,
                        description: quilttData.description,
                        type: quilttData.type,
                    };
                    return accountSummary;
                }
            }
        }

        return null;
    }, [
        selectedMergeAccountId,
        availableMergeAccounts,
        isMerged,
        account,
        accountWithExtras,
    ]);

    const isSpinwheel = account.provider === Provider.SPINWHEEL;
    const [isMerging, setIsMerging] = useState(false);
    const toast = useToastController();

    // Determine if this is a credit account
    // For merged Quiltt accounts, check the Spinwheel account type
    // For Spinwheel accounts, check the account type directly
    const isCreditAccount = useMemo(() => {
        if (account.provider === Provider.SPINWHEEL) {
            return account.type?.toLowerCase() === 'credit';
        } else if (account.provider === Provider.QUILTT && isMerged) {
            // For merged Quiltt accounts, check the Spinwheel account type
            const spinwheelAccount = accountWithExtras.spinwheelAccount;
            if (spinwheelAccount != null) {
                // Check if it has spinwheelCreditCardId (credit card) vs spinwheelLoanId (loan)
                return 'spinwheelCreditCardId' in spinwheelAccount;
            }
            // Fallback to account type
            return account.type?.toLowerCase() === 'credit';
        }
        return account.type?.toLowerCase() === 'credit';
    }, [account, isMerged, accountWithExtras]);

    const { setSpinwheelCreditCardQuilttAccountIdAsync } =
        useSetSpinwheelCreditCardQuilttAccountIdAndRefetch(
            membershipDetails.id,
        );
    const { setSpinwheelLoanQuilttAccountIdAsync } =
        useSetSpinwheelLoanQuilttAccountIdAndRefetch(membershipDetails.id);

    const getAccountDisplayName = (acc: AccountSummary) => {
        const accountNumber =
            acc.accountNumberMasked != null && acc.accountNumberMasked !== ''
                ? ` (****${acc.accountNumberMasked})`
                : '';
        return `${acc.name ?? 'Unnamed'}${accountNumber}`;
    };

    const handleMerge = async () => {
        console.log('[AccountMergeModal] handleMerge called');
        // Check if un-merge is selected (special value "UNMERGE")
        // If account is already merged, treat this as an un-merge operation
        const isUnmerge = isMerged || selectedMergeAccountId === 'UNMERGE';
        console.log('[AccountMergeModal] Initial check:', {
            isUnmerge,
            selectedMergeAccountId,
            hasSelectedMergeAccount: !!selectedMergeAccount,
            hasAccountUserId:
                account.userId != null && account.userId.length > 0,
        });

        if (
            !isUnmerge &&
            (selectedMergeAccount == null || account.userId == null)
        ) {
            console.log(
                '[AccountMergeModal] Early return - missing required data',
            );
            return;
        }

        try {
            setIsMerging(true);

            console.log('[AccountMergeModal] Account provider check:', {
                accountProvider: account.provider,
                isMerged,
                accountId: account.id,
            });

            // Determine which account is the Spinwheel account
            // If the main account is Spinwheel, use it
            // If the main account is Quiltt but merged, use the Spinwheel account from the merge
            let spinwheelAccountId: string;
            let userId: string;

            if (account.provider === Provider.SPINWHEEL) {
                console.log('[AccountMergeModal] Account is Spinwheel');
                if (account.userId == null || account.userId === '') {
                    toast.show(
                        'User ID is required',
                        MutationErrorToastOptions(),
                    );
                    return;
                }
                spinwheelAccountId = account.id;
                userId = account.userId;
            } else if (account.provider === Provider.QUILTT) {
                if (isMerged) {
                    console.log(
                        '[AccountMergeModal] Account is Quiltt and merged',
                    );
                    // If Quiltt account is merged, get the Spinwheel account ID from the merge
                    const spinwheelAccount = accountWithExtras.spinwheelAccount;
                    if (
                        spinwheelAccount?.id == null ||
                        spinwheelAccount.id === '' ||
                        account.userId == null ||
                        account.userId === ''
                    ) {
                        toast.show(
                            'Unable to find Spinwheel account for merge',
                            MutationErrorToastOptions(),
                        );
                        return;
                    }
                    spinwheelAccountId = spinwheelAccount.id;
                    userId = account.userId;
                } else {
                    // If Quiltt account is NOT merged, we need to merge it with a Spinwheel account
                    // The selected account should be the Spinwheel account
                    console.log(
                        '[AccountMergeModal] Account is Quiltt, merging with selected Spinwheel account',
                    );
                    if (selectedMergeAccount?.provider !== Provider.SPINWHEEL) {
                        toast.show(
                            'Please select a Spinwheel account to merge with',
                            MutationErrorToastOptions(),
                        );
                        return;
                    }
                    if (
                        selectedMergeAccount.userId == null ||
                        selectedMergeAccount.userId === ''
                    ) {
                        toast.show(
                            'Selected account missing user ID',
                            MutationErrorToastOptions(),
                        );
                        return;
                    }
                    // Use the selected Spinwheel account as the spinwheelAccountId
                    spinwheelAccountId = selectedMergeAccount.id;
                    userId = selectedMergeAccount.userId;
                }
            } else {
                console.log(
                    '[AccountMergeModal] Unsupported account provider:',
                    account.provider,
                );
                toast.show(
                    'Only Spinwheel and Quiltt accounts can be merged',
                    MutationErrorToastOptions(),
                );
                return;
            }

            console.log('[AccountMergeModal] Determined IDs:', {
                spinwheelAccountId,
                userId,
            });

            // Handle un-merge
            if (isUnmerge) {
                const mergeBody = {
                    userId,
                    spinwheelAccountId,
                    quilttAccountId: null,
                };

                if (isCreditAccount) {
                    await setSpinwheelCreditCardQuilttAccountIdAsync(mergeBody);
                } else {
                    await setSpinwheelLoanQuilttAccountIdAsync(mergeBody);
                }

                toast.show(
                    'Accounts un-merged successfully!',
                    MutationSuccessToastOptions,
                );
                onClose();
                return;
            }

            // Check if the selected account is already the merged account (only if account is already merged)
            console.log('[AccountMergeModal] Merge check:', {
                isMerged,
                isUnmerge,
                accountProvider: account.provider,
                accountId: account.id,
                selectedMergeAccountId,
                selectedMergeAccount: selectedMergeAccount
                    ? {
                          id: selectedMergeAccount.id,
                          provider: selectedMergeAccount.provider,
                          name: selectedMergeAccount.name,
                      }
                    : null,
                accountWithExtras: {
                    hasSpinwheelAccount:
                        accountWithExtras.spinwheelAccount != null,
                    hasQuilttAccount: accountWithExtras.quilttAccount != null,
                },
            });

            if (isMerged && selectedMergeAccount != null && !isUnmerge) {
                const currentMergedAccountId = getMergedAccountId();
                console.log(
                    '[AccountMergeModal] Account is merged, checking:',
                    {
                        currentMergedAccountId,
                        selectedMergeAccountId,
                        accountProvider: account.provider,
                    },
                );

                // Compare IDs - need to handle both Quiltt and Spinwheel cases
                if (account.provider === Provider.SPINWHEEL) {
                    // For Spinwheel accounts, check if selected account matches the merged Quiltt account
                    if (
                        currentMergedAccountId != null &&
                        currentMergedAccountId.length > 0 &&
                        selectedMergeAccount.id === currentMergedAccountId
                    ) {
                        console.log(
                            '[AccountMergeModal] Already merged - Spinwheel account matches merged Quiltt account',
                        );
                        toast.show(
                            'These accounts are already merged',
                            MutationErrorToastOptions(),
                        );
                        return;
                    }
                } else if (account.provider === Provider.QUILTT) {
                    // For Quiltt accounts, the merged account is the Spinwheel one
                    // Check if selected account matches the merged Spinwheel account ID
                    const spinwheelAccountId =
                        accountWithExtras.spinwheelAccount?.id;
                    console.log('[AccountMergeModal] Quiltt account check:', {
                        spinwheelAccountId,
                        selectedMergeAccountId,
                        match:
                            spinwheelAccountId != null &&
                            spinwheelAccountId.length > 0 &&
                            selectedMergeAccount.id === spinwheelAccountId,
                    });
                    if (
                        spinwheelAccountId != null &&
                        spinwheelAccountId.length > 0 &&
                        selectedMergeAccount.id === spinwheelAccountId
                    ) {
                        console.log(
                            '[AccountMergeModal] Already merged - Quiltt account matches merged Spinwheel account',
                        );
                        toast.show(
                            'These accounts are already merged',
                            MutationErrorToastOptions(),
                        );
                        return;
                    }
                }
            } else {
                console.log(
                    '[AccountMergeModal] Skipping merge check - not merged or unmerge selected',
                );
            }

            if (selectedMergeAccount == null) {
                toast.show(
                    'Please select an account to merge with',
                    MutationErrorToastOptions(),
                );
                return;
            }

            // Determine quilttAccountId based on which account is being merged
            let quilttAccountId: string;
            if (account.provider === Provider.SPINWHEEL) {
                // Merging Spinwheel account, so selectedMergeAccount should be Quiltt
                const selectedQuilttAccount =
                    selectedMergeAccount as AccountSummary & {
                        quilttAccount?: {
                            id: string;
                            quilttAccountId?: string;
                        };
                        quilttAccountId?: string;
                    };
                console.log(
                    '[AccountMergeModal] Spinwheel account - determining quilttAccountId:',
                    {
                        selectedMergeAccountId: selectedMergeAccount.id,
                        selectedAccountHasQuilttAccountId:
                            selectedQuilttAccount.quilttAccountId != null &&
                            selectedQuilttAccount.quilttAccountId.length > 0,
                        selectedQuilttAccountId:
                            selectedQuilttAccount.quilttAccountId,
                        selectedAccountHasQuilttAccount:
                            selectedQuilttAccount.quilttAccount != null,
                        quilttAccountId:
                            selectedQuilttAccount.quilttAccount
                                ?.quilttAccountId,
                        quilttAccountIdFromQuilttAccount:
                            selectedQuilttAccount.quilttAccount?.id,
                    },
                );
                // Use quilttAccountId field if available (the actual Quiltt account ID)
                // Otherwise fall back to quilttAccount.quilttAccountId or quilttAccount.id
                quilttAccountId =
                    selectedQuilttAccount.quilttAccountId ??
                    selectedQuilttAccount.quilttAccount?.quilttAccountId ??
                    selectedQuilttAccount.quilttAccount?.id ??
                    selectedMergeAccount.id;
            } else {
                // Merging Quiltt account, so quilttAccountId is from the quilttAccountId field or quilttAccount property
                console.log(
                    '[AccountMergeModal] Quiltt account - determining quilttAccountId:',
                    {
                        accountId: account.id,
                        accountHasQuilttAccountId:
                            accountWithExtras.quilttAccountId != null &&
                            accountWithExtras.quilttAccountId.length > 0,
                        quilttAccountId: accountWithExtras.quilttAccountId,
                        accountHasQuilttAccount:
                            accountWithExtras.quilttAccount != null,
                        quilttAccountIdFromQuilttAccount:
                            accountWithExtras.quilttAccount?.quilttAccountId,
                        quilttAccountIdFromQuilttAccountId:
                            accountWithExtras.quilttAccount?.id,
                    },
                );
                // Use quilttAccountId field if available (the actual Quiltt account ID)
                // Otherwise fall back to quilttAccount.quilttAccountId or quilttAccount.id
                quilttAccountId =
                    accountWithExtras.quilttAccountId ??
                    accountWithExtras.quilttAccount?.quilttAccountId ??
                    accountWithExtras.quilttAccount?.id ??
                    account.id;
            }

            console.log('[AccountMergeModal] Creating merge body:', {
                userId,
                spinwheelAccountId,
                quilttAccountId,
                quilttAccountIdStartsWithAcct:
                    quilttAccountId?.startsWith('acct_') ?? false,
            });

            const mergeBody = {
                userId,
                spinwheelAccountId,
                quilttAccountId,
            };

            // Determine if this is a credit account based ONLY on the Spinwheel account type
            // Find the Spinwheel account in allAccounts to check its type
            const spinwheelAccountInAllAccounts = allAccounts.find(
                (acc) =>
                    acc.id === spinwheelAccountId &&
                    acc.provider === Provider.SPINWHEEL,
            );

            // Check the account type - if it's "credit" it's a credit card, otherwise it's a loan
            const finalIsCreditAccount =
                spinwheelAccountInAllAccounts?.type?.toLowerCase() === 'credit';

            if (finalIsCreditAccount) {
                await setSpinwheelCreditCardQuilttAccountIdAsync(mergeBody);
            } else {
                await setSpinwheelLoanQuilttAccountIdAsync(mergeBody);
            }

            toast.show(
                'Accounts merged successfully!',
                MutationSuccessToastOptions,
            );
            onClose();
        } catch (error) {
            toast.show(
                'Error merging accounts',
                MutationErrorToastOptions(error),
            );
        } finally {
            setIsMerging(false);
        }
    };

    return (
        <GroveSimpleModal isOpen={isOpen} onClose={onClose} hasCloseButton>
            <YStack width={900} maxHeight="80vh">
                <ScrollView>
                    <YStack gap={16} padding={16}>
                        <BodyLargeEmphasized>Merge Account</BodyLargeEmphasized>
                        <YStack
                            padding={12}
                            borderRadius={8}
                            backgroundColor="$gray200"
                            gap={4}
                        >
                            <BodySmallRegular color="$gray900">
                                Note: You can only merge a Quiltt and Spinwheel
                                account within the same bucket (Save, Spend,
                                etc.) and accounts that are not already merged
                                with another account.
                            </BodySmallRegular>
                        </YStack>

                        <XStack gap={16} alignItems="flex-start">
                            {/* Left side - Selected account */}
                            <AccountInfoPanel
                                account={account}
                                title={
                                    isSpinwheel
                                        ? 'Spinwheel Account'
                                        : 'Quiltt Account'
                                }
                            />

                            {/* Right side - Select account to merge with */}
                            <YStack
                                flex={1}
                                gap={10}
                                padding={16}
                                borderRadius={16}
                                borderWidth={2}
                                borderStyle="dashed"
                                borderColor="$gray600"
                                backgroundColor="$gray100"
                            >
                                <BodyMediumRegular
                                    fontWeight="600"
                                    marginBottom={4}
                                >
                                    {isSpinwheel
                                        ? 'Quiltt Account'
                                        : 'Spinwheel Account'}
                                </BodyMediumRegular>

                                {(() => {
                                    // Debug logging
                                    console.log(
                                        '[AccountMergeModal] Before conditional render:',
                                        {
                                            isMerged,
                                            hasSelectedMergeAccount:
                                                selectedMergeAccount != null,
                                            selectedMergeAccountId,
                                            accountProvider: account.provider,
                                            accountId: account.id,
                                        },
                                    );
                                    return null;
                                })()}
                                {isMerged && selectedMergeAccount != null ? (
                                    <>
                                        {(() => {
                                            // Debug logging
                                            console.log(
                                                '[AccountMergeModal] Rendering merged account info:',
                                                {
                                                    isMerged,
                                                    hasSelectedMergeAccount:
                                                        selectedMergeAccount !=
                                                        null,
                                                    selectedMergeAccountId,
                                                    accountProvider:
                                                        account.provider,
                                                    spinwheelAccountId:
                                                        account.provider ===
                                                        Provider.QUILTT
                                                            ? accountWithExtras
                                                                  .spinwheelAccount
                                                                  ?.id
                                                            : account.id,
                                                },
                                            );
                                            return null;
                                        })()}
                                        <BodyMediumRegular
                                            color="$gray900"
                                            marginBottom={8}
                                        >
                                            This account is already merged with:
                                        </BodyMediumRegular>
                                        <YStack
                                            marginTop={8}
                                            padding={12}
                                            borderRadius={8}
                                            backgroundColor="$gray200"
                                        >
                                            <YStack gap={6}>
                                                <InfoRow
                                                    label="Account Nickname"
                                                    value={
                                                        selectedMergeAccount.name ??
                                                        'N/A'
                                                    }
                                                />
                                                <InfoRow
                                                    label="Institution"
                                                    value={
                                                        selectedMergeAccount.institutionName ??
                                                        'N/A'
                                                    }
                                                />
                                                <InfoRow
                                                    label="Account Number"
                                                    value={
                                                        selectedMergeAccount.accountNumberMasked !=
                                                            null &&
                                                        selectedMergeAccount.accountNumberMasked !==
                                                            ''
                                                            ? `****${selectedMergeAccount.accountNumberMasked}`
                                                            : 'N/A'
                                                    }
                                                />
                                                <InfoRow
                                                    label="Category"
                                                    value={
                                                        selectedMergeAccount.category !=
                                                        null
                                                            ? selectedMergeAccount.category
                                                                  .replace(
                                                                      /_/g,
                                                                      ' ',
                                                                  )
                                                                  .toLowerCase()
                                                                  .replace(
                                                                      /\b\w/g,
                                                                      (c) =>
                                                                          c.toUpperCase(),
                                                                  )
                                                            : 'N/A'
                                                    }
                                                />
                                                <InfoRow
                                                    label="Balance"
                                                    value={
                                                        selectedMergeAccount.currentBalanceCents !=
                                                        null
                                                            ? centsToFormattedDollars(
                                                                  selectedMergeAccount.currentBalanceCents,
                                                              )
                                                            : 'N/A'
                                                    }
                                                />
                                                {selectedMergeAccount.provider ===
                                                    Provider.SPINWHEEL && (
                                                    <>
                                                        {selectedMergeAccount.minimumPayment !=
                                                            null && (
                                                            <InfoRow
                                                                label="Min Payment"
                                                                value={centsToFormattedDollars(
                                                                    selectedMergeAccount.minimumPayment,
                                                                )}
                                                            />
                                                        )}
                                                        {selectedMergeAccount.interestRate !=
                                                            null &&
                                                            selectedMergeAccount.interestRate !==
                                                                undefined && (
                                                                <InfoRow
                                                                    label="Interest Rate"
                                                                    value={`${selectedMergeAccount.interestRate}%`}
                                                                />
                                                            )}
                                                    </>
                                                )}
                                                {selectedMergeAccount.description !=
                                                    null &&
                                                    selectedMergeAccount
                                                        .description.length >
                                                        0 && (
                                                        <InfoRow
                                                            label="Description"
                                                            value={
                                                                selectedMergeAccount.description
                                                            }
                                                        />
                                                    )}
                                                <InfoRow
                                                    label="Account Type"
                                                    value={
                                                        selectedMergeAccount.type ??
                                                        selectedMergeAccount.rawType ??
                                                        'N/A'
                                                    }
                                                />
                                                {isMerged &&
                                                    (() => {
                                                        // Get Spinwheel Account ID - if main account is Quiltt, get from spinwheelAccount, otherwise use account.id
                                                        const spinwheelAccountId =
                                                            account.provider ===
                                                            Provider.QUILTT
                                                                ? accountWithExtras
                                                                      .spinwheelAccount
                                                                      ?.id
                                                                : account.id;
                                                        console.log(
                                                            '[AccountMergeModal] Spinwheel Account ID check:',
                                                            {
                                                                isMerged,
                                                                accountProvider:
                                                                    account.provider,
                                                                spinwheelAccountId,
                                                                hasSpinwheelAccount:
                                                                    !!accountWithExtras.spinwheelAccount,
                                                                spinwheelAccountIdFromExtras:
                                                                    accountWithExtras
                                                                        .spinwheelAccount
                                                                        ?.id,
                                                                accountId:
                                                                    account.id,
                                                            },
                                                        );
                                                        return spinwheelAccountId !=
                                                            null &&
                                                            spinwheelAccountId !==
                                                                '' ? (
                                                            <InfoRow
                                                                label="Spinwheel Account ID"
                                                                value={
                                                                    spinwheelAccountId
                                                                }
                                                            />
                                                        ) : null;
                                                    })()}
                                            </YStack>
                                        </YStack>
                                    </>
                                ) : availableMergeAccounts.length === 0 ? (
                                    <BodyMediumRegular color="$gray900">
                                        No{' '}
                                        {isSpinwheel ? 'Quiltt' : 'Spinwheel'}{' '}
                                        accounts available to merge
                                    </BodyMediumRegular>
                                ) : (
                                    <>
                                        <YStack gap={8}>
                                            <BodySmallRegular color="$gray900">
                                                Select account to merge with:
                                            </BodySmallRegular>
                                            <select
                                                value={
                                                    selectedMergeAccountId ?? ''
                                                }
                                                onChange={(e) => {
                                                    setSelectedMergeAccountId(
                                                        e.target.value || null,
                                                    );
                                                }}
                                                style={{
                                                    padding: 12,
                                                    borderRadius: 8,
                                                    borderWidth: 1,
                                                    borderColor: '#ccc',
                                                    fontSize: 16,
                                                    width: '100%',
                                                }}
                                            >
                                                <option value="">
                                                    Select an account...
                                                </option>
                                                {isMerged && (
                                                    <option value="UNMERGE">
                                                        Un-merge
                                                    </option>
                                                )}
                                                {availableMergeAccounts.map(
                                                    (acc) => (
                                                        <option
                                                            key={acc.id}
                                                            value={acc.id}
                                                        >
                                                            {getAccountDisplayName(
                                                                acc,
                                                            )}
                                                        </option>
                                                    ),
                                                )}
                                            </select>
                                        </YStack>

                                        {selectedMergeAccount != null && (
                                            <YStack
                                                marginTop={8}
                                                padding={12}
                                                borderRadius={8}
                                                backgroundColor="$gray200"
                                            >
                                                <YStack gap={6}>
                                                    <InfoRow
                                                        label="Account Nickname"
                                                        value={
                                                            selectedMergeAccount.name ??
                                                            'N/A'
                                                        }
                                                    />
                                                    <InfoRow
                                                        label="Institution"
                                                        value={
                                                            selectedMergeAccount.institutionName ??
                                                            'N/A'
                                                        }
                                                    />
                                                    <InfoRow
                                                        label="Account Number"
                                                        value={
                                                            selectedMergeAccount.accountNumberMasked !=
                                                                null &&
                                                            selectedMergeAccount
                                                                .accountNumberMasked
                                                                .length > 0
                                                                ? `****${selectedMergeAccount.accountNumberMasked}`
                                                                : 'N/A'
                                                        }
                                                    />
                                                    <InfoRow
                                                        label="Category"
                                                        value={
                                                            selectedMergeAccount.category !=
                                                            null
                                                                ? selectedMergeAccount.category
                                                                      .replace(
                                                                          /_/g,
                                                                          ' ',
                                                                      )
                                                                      .toLowerCase()
                                                                      .replace(
                                                                          /\b\w/g,
                                                                          (c) =>
                                                                              c.toUpperCase(),
                                                                      )
                                                                : 'N/A'
                                                        }
                                                    />
                                                    <InfoRow
                                                        label="Balance"
                                                        value={
                                                            selectedMergeAccount.currentBalanceCents !=
                                                                null &&
                                                            selectedMergeAccount.currentBalanceCents !==
                                                                undefined
                                                                ? centsToFormattedDollars(
                                                                      selectedMergeAccount.currentBalanceCents,
                                                                  )
                                                                : 'N/A'
                                                        }
                                                    />
                                                    {selectedMergeAccount.provider ===
                                                        Provider.SPINWHEEL && (
                                                        <>
                                                            {selectedMergeAccount.minimumPayment !=
                                                                null &&
                                                                selectedMergeAccount.minimumPayment !==
                                                                    undefined && (
                                                                    <InfoRow
                                                                        label="Min Payment"
                                                                        value={centsToFormattedDollars(
                                                                            selectedMergeAccount.minimumPayment,
                                                                        )}
                                                                    />
                                                                )}
                                                            {selectedMergeAccount.interestRate !=
                                                                null &&
                                                                selectedMergeAccount.interestRate !==
                                                                    undefined && (
                                                                    <InfoRow
                                                                        label="Interest Rate"
                                                                        value={`${selectedMergeAccount.interestRate}%`}
                                                                    />
                                                                )}
                                                        </>
                                                    )}
                                                    {selectedMergeAccount.description !=
                                                        null &&
                                                        selectedMergeAccount
                                                            .description
                                                            .length > 0 && (
                                                            <InfoRow
                                                                label="Description"
                                                                value={
                                                                    selectedMergeAccount.description
                                                                }
                                                            />
                                                        )}
                                                    <InfoRow
                                                        label="Account Type"
                                                        value={
                                                            selectedMergeAccount.type ??
                                                            selectedMergeAccount.rawType ??
                                                            'N/A'
                                                        }
                                                    />
                                                </YStack>
                                            </YStack>
                                        )}
                                    </>
                                )}
                            </YStack>
                        </XStack>
                    </YStack>
                </ScrollView>
                <XStack
                    gap={12}
                    padding={16}
                    borderTopWidth={1}
                    borderColor="$gray600"
                >
                    <Button
                        flex={1}
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.SMALL}
                        tone="neutral"
                        text="Cancel"
                        onPress={onClose}
                    />
                    <Button
                        flex={1}
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.SMALL}
                        tone="positive"
                        text={
                            isMerging
                                ? 'Processing...'
                                : isMerged
                                  ? 'Un-merge Accounts'
                                  : 'Merge Accounts'
                        }
                        onPress={handleMerge}
                        disabled={
                            isMerging ||
                            account.userId == null ||
                            account.userId.length === 0 ||
                            (!isMerged && selectedMergeAccount == null)
                        }
                        icon={isMerging ? <Spinner /> : undefined}
                    />
                </XStack>
            </YStack>
        </GroveSimpleModal>
    );
};
